import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import useSWR from "swr";
import { Utils } from "../common/utils";
import { CustomSelectOption } from "Components/Common/CustomSelect";
import { DomainCompetency } from "Stores/courseStore";
import { EAIClient } from "Services/EAIClient";
import { appStore } from "Stores/index";
import * as Sentry from "@sentry/react";

const STORAGE_KEY = "competencyMatchingProgress";
const AUTO_SAVE_INTERVAL = 60000; // 1 minute in milliseconds
const STEP_STORAGE_KEY = "competencyMatchingStep";
const TOTAL_STEPS = 3;

const clientConfigs = Utils.getEAIClientConfigs();

export interface CompetencyMatchingResponse {
    data: CompetencyMatchingSource[] | DomainCompetency[];
    code: number;
    message: string;
}

export interface CompetencyMatchingSource {
    source_ruuid: string;
    source_name: string;
    total_items: number;
    total_mapped_items: number;
    mapped_percentage: number;
    standard_level: number;
}

interface CompetencyMatchingContextType {
    sourceOptions: CustomSelectOption[];
    sourcesLoading: boolean;
    sourcesError: any;
    sourcesMutate: () => Promise<any>;
    categoryOptions: CustomSelectOption[];
    categoriesLoading: boolean;
    categoriesError: any;
    categoriesMutate: () => Promise<any>;
    standardCategoryOptions: CustomSelectOption[];
    standardsCategoriesLoading: boolean;
    standardsCategoriesError: any;
    standardsCategoriesMutate: () => Promise<any>;
    sourcesData: CompetencyMatchingResponse | undefined;
    categoriesData: CompetencyMatchingResponse | undefined;
    standardsCategoriesData: CompetencyMatchingResponse | undefined;
    selectedSource: string;
    selectedCategory: string;
    selectedStandardCategory: string;
    setSelectedSource: (source: string) => void;
    setSelectedCategory: (category: string) => void;
    setSelectedStandardCategory: (standardCategory: string) => void;
    selectedCategoryData: DomainCompetency | undefined;
    selectedStandardCategoryData: DomainCompetency | undefined;
    lastSaved: string | null;
    clearAllSavedData: () => void;
    currentStep: number;
    handleNextStep: () => void;
    handlePreviousStep: () => void;
    isCompleted: boolean;
}

const CompetencyMatchingContext = createContext<CompetencyMatchingContextType | undefined>(undefined);

export const useCompetencyMatchingContext = () => {
    const context = useContext(CompetencyMatchingContext);
    if (context === undefined) {
        throw new Error("useCompetencyMatchingContext must be used within a CompetencyMatchingProvider");
    }
    return context;
};

interface CompetencyMatchingProviderProps {
    children: ReactNode;
}

export const CompetencyMatchingProvider = ({ children }: CompetencyMatchingProviderProps) => {
    const [sources, setSources] = useState<CompetencyMatchingSource[]>([]);
    const [categories, setCategories] = useState<DomainCompetency[]>([]);
    const [standardsCategories, setStandardsCategories] = useState<DomainCompetency[]>([]);
    const [lastSaved, setLastSaved] = useState<string | null>(null);
    const [selectedSource, setSelectedSource] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedStandardCategory, setSelectedStandardCategory] = useState<string>("");
    const [isCompleted, setIsCompleted] = useState(false);
    const [currentStep, setCurrentStep] = useState(() => {
        const savedStep = localStorage.getItem(STEP_STORAGE_KEY);
        return savedStep ? parseInt(savedStep) : 1;
    });

    const saveStep = useCallback((step: number) => {
        localStorage.setItem(STEP_STORAGE_KEY, step.toString());
    }, []);

    const handleNextStep = useCallback(() => {
        if (currentStep < TOTAL_STEPS) {
            const nextStep = currentStep + 1;
            setCurrentStep(nextStep);
            saveStep(nextStep);
        }
    }, [currentStep, saveStep]);

    const handlePreviousStep = useCallback(() => {
        if (currentStep > 1) {
            const prevStep = currentStep - 1;
            setCurrentStep(prevStep);
            saveStep(prevStep);
        }
    }, [currentStep, saveStep]);

    const saveProgress = useCallback((source: string, category: string, standardCategory: string) => {
        const timestamp = new Date().toISOString();
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            selectedSource: source,
            selectedCategory: category,
            selectedStandardCategory: standardCategory,
            lastUpdated: timestamp
        }));
        setLastSaved(timestamp);
    }, []);

    const handleSetSelectedSource = useCallback((source: string) => {
        setSelectedSource(source);
        saveProgress(source, selectedCategory, selectedStandardCategory);
    }, [selectedCategory, selectedStandardCategory, saveProgress]);

    const handleSetSelectedCategory = useCallback((category: string) => {
        setSelectedCategory(category);
        saveProgress(selectedSource, category, selectedStandardCategory);
    }, [selectedSource, selectedStandardCategory, saveProgress]);

    const handleSetSelectedStandardCategory = useCallback((standardCategory: string) => {
        setSelectedStandardCategory(standardCategory);
        saveProgress(selectedSource, selectedCategory, standardCategory);
    }, [selectedSource, selectedCategory, saveProgress]);

    const clearAllSavedData = useCallback(() => {
        const client = new EAIClient();
        client.ax.patch(`${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/domain_competency/match`, [
            {
              enflux_standard_competency_ruuid: selectedStandardCategory,
              domain_competency_ruuid: selectedCategory
            }
          ]).then(response => {
            if (!response?.data) {
                throw new Error("Invalid response from server");
            }
            setIsCompleted(true);
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STEP_STORAGE_KEY);
            localStorage.removeItem("competencyMatchingCategories");
            localStorage.removeItem("competencyMatchingExpanded");

            setSelectedSource("");
            setSelectedCategory("");
            setSelectedStandardCategory("");
            setLastSaved(null);
            setCurrentStep(1);
        }).catch((error) => {
            Sentry.captureException(error);
            const errorMessage = error?.response?.data?.result?.message || "Failed to save competency matching";
            appStore.addError(error, errorMessage);
        });
    }, [selectedStandardCategory, selectedCategory]);

    useEffect(() => {
        const savedProgress = localStorage.getItem(STORAGE_KEY);
        if (savedProgress) {
            try {
                const { selectedSource, selectedCategory, selectedStandardCategory, lastUpdated } = JSON.parse(savedProgress);
                setSelectedSource(selectedSource || "");
                setSelectedCategory(selectedCategory || "");
                setSelectedStandardCategory(selectedStandardCategory || "");
                setLastSaved(lastUpdated || null);
            } catch (error) {
                console.error("Error loading saved progress:", error);
            }
        }
    }, []);

    // Auto-save effect
    useEffect(() => {
        const autoSaveInterval = setInterval(() => {
            if (selectedSource || selectedCategory || selectedStandardCategory) {
                saveProgress(selectedSource, selectedCategory, selectedStandardCategory);
            }
        }, AUTO_SAVE_INTERVAL);

        return () => clearInterval(autoSaveInterval);
    }, [selectedSource, selectedCategory, selectedStandardCategory, saveProgress]);

    const {
        data: sourcesData,
        error: sourcesError,
        isLoading: sourcesLoading,
        mutate: sourcesMutate,
    } = useSWR<CompetencyMatchingResponse>(
        `${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/domain_competency/sources`
    );

    const {
        data: categoriesData,
        error: categoriesError,
        isLoading: categoriesLoading,
        mutate: categoriesMutate,
    } = useSWR<CompetencyMatchingResponse>(
        selectedSource
            ? `${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/domain_competency`
            : null
    );

    const {
        data: standardsCategoriesData,
        error: standardsCategoriesError,
        isLoading: standardsCategoriesLoading,
        mutate: standardsCategoriesMutate,
    } = useSWR<CompetencyMatchingResponse>(
        selectedSource
            ? `${clientConfigs.endpoint}/schools/${clientConfigs.school}/curriculum_mapping/standard_domain_competency`
            : null
    );

    const sourceOptions: CustomSelectOption[] = sources.map((source) => {
        return {
            value: source.source_ruuid ?? uuidv4(),
            label: source.source_name,
            status: source.mapped_percentage > 0 ? "In progress" : "Not started",
            percentage: source.mapped_percentage,
        };
    });

    const categoryOptions: CustomSelectOption[] = categories.map(
        (category) => ({
            value: category.ruuid,
            label: category.standard_name,
        })
    );

    const standardCategoryOptions: CustomSelectOption[] =
        standardsCategories.map((standardCategory) => ({
            value: standardCategory.ruuid,
            label: standardCategory.standard_name,
        }));

    const selectedCategoryData = categoriesData?.data
        ? (categoriesData.data as DomainCompetency[]).find(
            (category) => category.ruuid === selectedCategory
          )
        : undefined;

    const selectedStandardCategoryData = standardsCategoriesData?.data
        ? (standardsCategoriesData.data as DomainCompetency[]).find(
            (category) => category.ruuid === selectedStandardCategory
          )
        : undefined;

    useEffect(() => {
        if (sourcesData?.data) {
            try {
                setSources(sourcesData.data.map((source) => source));
            } catch (error) {
                appStore.addError(error, "Failed to process sources data");
            }
        }

        if (categoriesData?.data) {
            try {
                setCategories(categoriesData.data.map((category) => category));
            } catch (error) {
                appStore.addError(error, "Failed to process categories data");
            }
        }

        if (standardsCategoriesData?.data) {
            try {
                setStandardsCategories(
                    standardsCategoriesData.data.map(
                        (standardCategory) => standardCategory
                    )
                );
            } catch (error) {
                appStore.addError(error, "Failed to process standards categories data");
            }
        }
    }, [sourcesData, categoriesData, standardsCategoriesData]);

    const value = {
        sourceOptions,
        sourcesLoading,
        sourcesError,
        sourcesMutate,
        categoryOptions,
        categoriesLoading,
        categoriesError,
        categoriesMutate,
        standardCategoryOptions,
        standardsCategoriesLoading,
        standardsCategoriesError,
        standardsCategoriesMutate,
        sourcesData,
        categoriesData,
        standardsCategoriesData,
        selectedSource,
        selectedCategory,
        selectedStandardCategory,
        setSelectedSource: handleSetSelectedSource,
        setSelectedCategory: handleSetSelectedCategory,
        setSelectedStandardCategory: handleSetSelectedStandardCategory,
        selectedCategoryData,
        selectedStandardCategoryData,
        lastSaved,
        clearAllSavedData,
        currentStep,
        handleNextStep,
        handlePreviousStep,
        isCompleted
    };

    return (
        <CompetencyMatchingContext.Provider value={value}>
            {children}
        </CompetencyMatchingContext.Provider>
    );
};